import { useRef, useState } from "react";
import { UploadCloud, Film, Image as ImageIcon, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const ACCEPT = "video/mp4,video/webm,video/quicktime,image/jpeg,image/png,image/webp";

export const uploadChunked = async (file, onProgress) => {
  const { data: init } = await api.post("/uploads/chunked/init", {
    filename: file.name, content_type: file.type, size: file.size,
  });
  const total = Math.ceil(file.size / init.chunk_size);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * init.chunk_size, (i + 1) * init.chunk_size);
    await api.put(`/uploads/chunked/${init.upload_id}?index=${i}`, blob, {
      headers: { "Content-Type": "application/octet-stream" },
    });
    onProgress?.(Math.round(((i + 1) / total) * 95));
  }
  const { data } = await api.post(`/uploads/chunked/${init.upload_id}/complete`);
  onProgress?.(100);
  return data;
};

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export const DeliverableUploader = ({ files, onChange, disabled }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [currentName, setCurrentName] = useState("");

  const handleFiles = async (list) => {
    const picked = Array.from(list || []).filter((f) => ACCEPT.includes(f.type));
    if (!picked.length) { toast.error("Please choose an MP4/MOV/WebM video or an image"); return; }
    for (const f of picked) {
      if (f.size > 500 * 1024 * 1024) { toast.error(`${f.name} is over 500 MB`); continue; }
      setCurrentName(f.name);
      setProgress(0);
      try {
        const up = await uploadChunked(f, setProgress);
        onChange([...(files || []), { url: up.url, content_type: up.content_type, filename: up.filename || f.name, size: up.size }]);
        toast.success(`${f.name} uploaded`);
      } catch (e) {
        toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail) || "upload failed"}`);
      }
    }
    setProgress(null);
    setCurrentName("");
  };

  const busy = progress !== null;

  return (
    <div className="space-y-3" data-testid="deliverable-uploader">
      <div
        role="button"
        tabIndex={0}
        onClick={() => !busy && !disabled && inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!busy) handleFiles(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed p-6 text-center transition-colors cursor-pointer ${
          dragOver ? "border-nexus-blue bg-nexus-blue/5" : "border-white/15 bg-white/[0.02] hover:border-white/30"
        } ${busy || disabled ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="deliverable-dropzone"
      >
        <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="deliverable-file-input" />
        <UploadCloud className="h-8 w-8 mx-auto text-nexus-blue mb-2" />
        <div className="font-semibold text-white">{busy ? `Uploading ${currentName}…` : "Upload your finished video"}</div>
        <div className="text-xs text-zinc-500 mt-1">MP4, MOV or WebM · up to 500 MB · drag & drop or click</div>
        {busy && (
          <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="upload-progress">
            <div className="h-full nexus-gradient transition-[width] duration-200" style={{ width: `${progress}%` }} />
          </div>
        )}
        {busy && <div className="text-xs text-zinc-400 mt-1">{progress}%</div>}
      </div>

      {(files || []).length > 0 && (
        <ul className="space-y-2" data-testid="deliverable-file-list">
          {files.map((f, i) => (
            <li key={f.url} className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/5" data-testid={`deliverable-file-${i}`}>
              {f.content_type?.startsWith("video/") ? <Film className="h-4 w-4 text-nexus-pink shrink-0" /> : <ImageIcon className="h-4 w-4 text-nexus-blue shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{f.filename}</div>
                <div className="text-[11px] text-zinc-500">{fmtSize(f.size || 0)}</div>
              </div>
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              {!disabled && (
                <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-white" aria-label="Remove" data-testid={`remove-deliverable-${i}`}>
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const DeliverableGallery = ({ files, toAbsolute }) => {
  if (!files?.length) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="deliverable-gallery">
      {files.map((f, i) => {
        const src = toAbsolute(f.url);
        return (
          <figure key={f.url} className="rounded-xl overflow-hidden bg-black border border-white/10" data-testid={`deliverable-media-${i}`}>
            {f.content_type?.startsWith("video/") ? (
              <video src={src} controls playsInline preload="metadata" className="w-full max-h-[420px] bg-black" data-testid={`deliverable-video-${i}`} />
            ) : (
              <img src={src} alt={f.filename} className="w-full max-h-[420px] object-contain bg-black" />
            )}
            <figcaption className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-zinc-400">
              <span className="truncate">{f.filename}</span>
              <a href={src} download={f.filename} target="_blank" rel="noreferrer" className="text-nexus-blue hover:underline shrink-0" data-testid={`download-deliverable-${i}`}>Download</a>
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
};
